import { useState } from "react";
import apiClient from "api/apiClient";
import { Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import useMessageToast from "hooks/useMessageToast";

const passwordValidation = Yup.string()
  .required("New password is required")
  .min(8, "Password must be at least 8 characters")
  .matches(/(?=.*[0-9])/, "Must contain at least one number")
  .matches(/(?=.*[a-z])/, "Must contain a lowercase letter")
  .matches(/(?=.*[A-Z])/, "Must contain an uppercase letter")
  .matches(/(?=.*[!@#$%^&*])/, "Must contain a special character");

const ChangePasswordForm = () => {
  const [status, setStatus] = useState("");
  useMessageToast(status, "info");
  const [show, setShow] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const toggleVisibility = (field) => {
    setShow((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const validationSchema = Yup.object().shape({
    currentPassword: Yup.string().required("Current password is required"),
    newPassword: passwordValidation.notOneOf(
      [Yup.ref("currentPassword")],
      "New password must be different from your current password",
    ),
    confirmNewPassword: Yup.string()
      .oneOf([Yup.ref("newPassword"), null], "Passwords must match")
      .required("Confirm your new password"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setStatus("");

    try {
      await apiClient.post(
        `${process.env.REACT_APP_BASE_URL}/user/change-password`,
        {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
        { withCredentials: true },
      );
      setStatus("✅ Password updated successfully.");
      resetForm();
    } catch (error) {
      setStatus(
        error.response?.data?.message || "❌ Failed to change password.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="settings-section">
      <h3>Change Password</h3>
      <Formik
        initialValues={{
          currentPassword: "",
          newPassword: "",
          confirmNewPassword: "",
        }}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, handleChange }) => (
          <Form>
            {/* Current Password */}
            <div className="password-field-wrapper">
              <input
                type={show.current ? "text" : "password"}
                name="currentPassword"
                placeholder="Current Password"
                value={values.currentPassword}
                onChange={handleChange}
              />
              <span onClick={() => toggleVisibility("current")}>
                {show.current ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </span>
            </div>
            <ErrorMessage
              name="currentPassword"
              component="div"
              className="errorMessage"
            />

            {/* New Password */}
            <div className="password-field-wrapper">
              <input
                type={show.new ? "text" : "password"}
                name="newPassword"
                placeholder="New Password"
                value={values.newPassword}
                onChange={handleChange}
              />
              <span onClick={() => toggleVisibility("new")}>
                {show.new ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </span>
            </div>
            <ErrorMessage
              name="newPassword"
              component="div"
              className="errorMessage"
            />

            {/* Confirm New Password */}
            <div className="password-field-wrapper">
              <input
                type={show.confirm ? "text" : "password"}
                name="confirmNewPassword"
                placeholder="Confirm New Password"
                value={values.confirmNewPassword}
                onChange={handleChange}
              />
              <span onClick={() => toggleVisibility("confirm")}>
                {show.confirm ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
              </span>
            </div>
            <ErrorMessage
              name="confirmNewPassword"
              component="div"
              className="errorMessage"
            />

            <button type="submit" disabled={isSubmitting}>
              Update Password
            </button>
            {status && <p>{status}</p>}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default ChangePasswordForm;
